import { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";

export const dailyNoteAttachmentUrl = (attachment) => {
  if (typeof attachment === "string") return attachment.startsWith("http") ? attachment : "";
  return attachment?.signedUrl || attachment?.previewUrl || attachment?.downloadUrl || attachment?.url || attachment?.fileUrl || "";
};

export const dailyNoteAttachmentLabel = (attachment, index) => typeof attachment === "string"
  ? `Attachment ${index + 1}`
  : attachment?.description || attachment?.fileName || attachment?.originalFileName || attachment?.name || `Attachment ${index + 1}`;

const isImageAttachment = (attachment, url, index) => {
  const type = typeof attachment === "object" ? attachment?.contentType || attachment?.type || attachment?.mimeType || "" : "";
  const signedPhotoWithoutMetadata = typeof attachment === "object" && attachment?.fileId && url && !type && !attachment?.fileName && !attachment?.name;
  return signedPhotoWithoutMetadata || String(type).startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|heif)(?:\?|$)/i.test(url || dailyNoteAttachmentLabel(attachment, index));
};

export default function DailyNoteAttachmentGallery({ attachments = [], onRemove, compact = false }) {
  const items = useMemo(() => (Array.isArray(attachments) ? attachments : []).map((attachment, index) => {
    const url = dailyNoteAttachmentUrl(attachment);
    return { attachment, url, label: dailyNoteAttachmentLabel(attachment, index), isImage: isImageAttachment(attachment, url, index), index };
  }), [attachments]);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [rotation, setRotation] = useState(0);
  const selected = previewIndex === null ? null : items[previewIndex];

  useEffect(() => {
    if (previewIndex !== null && previewIndex >= items.length) setPreviewIndex(items.length ? items.length - 1 : null);
  }, [items.length, previewIndex]);

  const openPreview = (index) => {
    if (!items[index]?.url) return;
    setRotation(0);
    setPreviewIndex(index);
  };

  const showPrevious = () => {
    setRotation(0);
    setPreviewIndex((index) => (index - 1 + items.length) % items.length);
  };

  const showNext = () => {
    setRotation(0);
    setPreviewIndex((index) => (index + 1) % items.length);
  };

  if (!items.length) return null;
  return (
    <>
      <div className={`daily-note-gallery${compact ? " compact" : ""}`}>
        {items.map((item) => <div className="daily-note-gallery-item" key={`${item.label}-${item.index}`}>
          <button type="button" className="daily-note-gallery-thumb" disabled={!item.url} onClick={() => openPreview(item.index)} title={`Preview ${item.label}`}>
            {item.isImage && item.url ? <img src={item.url} alt="" /> : <i className="ti ti-file-description" />}
            <span><i className="ti ti-eye" /></span>
          </button>
          <small title={item.label}>{item.label}</small>
          {onRemove ? <button className="daily-note-gallery-remove" type="button" aria-label={`Remove ${item.label}`} onClick={() => onRemove(item.index)}><i className="ti ti-x" /></button> : null}
        </div>)}
      </div>
      <Modal className="daily-note-gallery-modal" open={Boolean(selected)} onCancel={() => setPreviewIndex(null)} footer={null} centered width="min(960px, 94vw)" title={selected?.label || "Attachment Preview"} destroyOnClose>
        <div className="daily-note-gallery-toolbar">
          <span>{(previewIndex ?? 0) + 1} of {items.length}</span>
          <button type="button" title="Rotate left" disabled={!selected?.isImage} onClick={() => setRotation((value) => value - 90)}><i className="ti ti-rotate-2" /></button>
          <button type="button" title="Rotate right" disabled={!selected?.isImage} onClick={() => setRotation((value) => value + 90)}><i className="ti ti-rotate-clockwise-2" /></button>
          <a href={selected?.url} target="_blank" rel="noreferrer" title="Open attachment"><i className="ti ti-external-link" /></a>
        </div>
        <div className="daily-note-gallery-preview-shell">
          <button className="daily-note-gallery-nav previous" type="button" title="Previous attachment" aria-label="Previous attachment" disabled={items.length < 2} onClick={showPrevious}><i className="ti ti-chevron-left" /></button>
          <div className="daily-note-gallery-preview">
            {selected?.isImage ? <img src={selected.url} alt={selected.label} style={{ transform: `rotate(${rotation}deg)` }} /> : selected?.url ? <iframe src={selected.url} title={selected.label} /> : null}
          </div>
          <button className="daily-note-gallery-nav next" type="button" title="Next attachment" aria-label="Next attachment" disabled={items.length < 2} onClick={showNext}><i className="ti ti-chevron-right" /></button>
        </div>
      </Modal>
    </>
  );
}
